// Hooks & Context
import { useCallback, useEffect, useState } from "react";

import { UserPlus, Users } from "lucide-react";
import { toast } from "sonner";

// Components
import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { usePagination } from "../../hooks/usePagination";

import UserCreateDialog from "./component/UserCreateDialog";
import UserTable from "./component/UserTable";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useConfirm } from "../../context/ConfirmContext.jsx";

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const { refreshUsers } = useData();
  const api = useApi();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "",
    tenantId: "",
    allowedProjectIds: []
  });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);

  const pagination = usePagination(10);
  const { items: users, currentPage: page, pageSize, setPaginationData, setCurrentPage: setPage } = pagination;

  const loadUsers = useCallback(async () => {
    try {
      const response = await api.get(`/users`, {
        params: {
          page,
          size: pageSize,
          search: searchTerm.trim() || undefined,
          sortBy: sortBy || undefined,
          direction: direction || undefined,
          showDeleted
        }
      });
      setPaginationData(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to load users");
    }
  }, [api, page, pageSize, searchTerm, sortBy, direction, showDeleted, setPaginationData]);

  const createUser = async (e) => {
    e.preventDefault();
    const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.role?.name === "super_admin";
    if (isSuperAdmin && !newUser.tenantId) {
      toast.error("Please select a tenant for the new user");
      return;
    }
    try {
      await api.post(`/users`, newUser);
      loadUsers();
      refreshUsers();
      setNewUser({
        name: "",
        email: "",
        password: "",
        phone: "",
        role: ""
      });
      setIsCreateOpen(false);
      toast.success("User created successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to create user");
    }
  };

  const updateUser = async (e) => {
    e.preventDefault();
    const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.role?.name === "super_admin";
    if (isSuperAdmin && !editFormData.tenantId) {
      toast.error("Please select a tenant for the user");
      return;
    }
    try {
      await api.put(`/users/${editingUser.id}`, editFormData);
      loadUsers();
      refreshUsers();
      setEditingUser(null);
      setIsEditOpen(false);
      toast.success("User updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to update user");
    }
  };

  const confirm = useConfirm();

  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role?.id || user.role,
      tenantId: user.tenantId,
      isActive: user.isActive,
      allowedProjectIds: user.allowedProjectIds || []
    });
    setIsEditOpen(true);
  };

  const deleteUser = async (userId) => {
    const isConfirmed = await confirm({
      title: "Delete User",
      message: "Are you sure you want to delete this user? This action cannot be undone."
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/users/${userId}`);
      loadUsers();
      refreshUsers();
      toast.success("User deleted successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to delete user");
    }
  };

  const resetPassword = (userId) => {
    setUserToResetPassword(userId);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordResetOpen(true);
  };

  const handleConfirmResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim() || !confirmPassword.trim()) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      await api.put(`/users/${userToResetPassword}/password`, {
        newPassword: newPassword
      });
      toast.success("Password updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setPasswordResetOpen(false);
      setUserToResetPassword(null);
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  useEffect(() => {
    if (currentUser?.permissions?.user?.read) {
      loadUsers();
    }
  }, [currentUser, loadUsers]);

  if (!currentUser?.permissions?.user?.read) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-yale-blue-50 dark:bg-yale-blue-900/50 rounded-xl border border-dashed border-slate-200 dark:border-yale-blue-800">
        <div className="bg-slate-200 dark:bg-yale-blue-800 p-4 rounded-full mb-4">
          <UserPlus className="w-8 h-8 text-slate-400 dark:text-yale-blue-500" />
        </div>
        <h3 className="text-xl font-semibold text-yale-blue-900 dark:text-yale-blue-50 mb-2">Access Restricted</h3>
        <p className="text-yale-blue-500 dark:text-slate-400 max-w-xs mx-auto">
          You don't have permission to manage users. Please contact your system administrator for access.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 space-y-8">
        <PageHeader
          title="User Management"
          subtitle="Manage system users, roles, and security settings"
          actionLabel={currentUser?.permissions?.user?.create ? "Add New User" : null}
          onActionClick={() => setIsCreateOpen(true)}
          icon={UserPlus}
          pageIcon={Users}
          extraActions={
            <SearchInput
              placeholder="Search users..."
              className="md:w-72"
              onSearch={(val) => {
                setSearchTerm(val);
                setPage(0);
              }}
            />
          }
        />

        <UserCreateDialog
          open={isCreateOpen}
          onOpenChange={setIsCreateOpen}
          newUser={newUser}
          setNewUser={setNewUser}
          onSubmit={createUser}
        />

        {editingUser && (
          <UserCreateDialog
            open={isEditOpen}
            onOpenChange={setIsEditOpen}
            newUser={editFormData}
            setNewUser={setEditFormData}
            onSubmit={updateUser}
            isEdit={true}
          />
        )}



        {/* Reset Password Dialog */}
        <Dialog open={passwordResetOpen} onOpenChange={setPasswordResetOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Enter a new password for the user. It must be at least 6 characters long.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleConfirmResetPassword} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="reset-new-password">New Password</Label>
                <Input
                  id="reset-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-confirm-password">Confirm Password</Label>
                <Input
                  id="reset-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-0 pt-2">
                <Button type="button" variant="ghost" onClick={() => setPasswordResetOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Update Password
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <UserTable
          users={users}
          currentUser={currentUser}
          permissions={{
            canUpdate: currentUser?.permissions?.user?.update,
            canDelete: currentUser?.permissions?.user?.delete
          }}
          onEdit={handleEditClick}
          onDelete={deleteUser}
          onResetPassword={resetPassword}
          sortBy={sortBy}
          direction={direction}
          onSort={(key, dir) => {
            setSortBy(key);
            setDirection(dir);
          }}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          setCurrentPage={setPage}
        />
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>
    </div>
  );
};

export default UserManagement;
